import React from "react";
import {
  Search,
  Database,
  RefreshCw,
  Server,
  Clock,
  ArrowRight,
  ArrowLeft,
} from "lucide-react";

const NexusBrochure = () => {
  // Mock onNavigate function for the artifact
  const onNavigate = (destination) => {
    console.log(`Navigating to: ${destination}`);
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Back Button */}
      <button
        className="mb-6 flex items-center text-blue-600 hover:text-blue-800"
        onClick={() => onNavigate("modules")}
      >
        <ArrowLeft className="mr-2" size={16} />
        Back to Modules
      </button>

      <div className="flex flex-col w-full bg-white shadow-lg rounded-lg overflow-hidden border border-gray-200">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-900 to-blue-700 text-white p-6 flex justify-between items-center">
          <div className="flex items-center">
            <div className="w-24 h-16 mr-4">
              <img
                src="/workcliff_logo_white.png"
                alt="WorkCliff Nexus Logo"
                className="w-full h-full object-contain"
              />
            </div>
            <div>
              <h1 className="text-3xl font-bold">WorkCliff</h1>
              <h2 className="text-xl">NEXUS Migration Solution</h2>
            </div>
          </div>
          <div className="text-right max-w-md">
            <p className="text-lg leading-tight tracking-tight">
              Revolutionizing Workday implementation
              <br />
              with AI-powered migration
            </p>
          </div>
        </div>

        {/* Main Content */}
        <div className="p-4">
          {/* Two Column Layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-0">
            {/* Left Column */}
            <div>
              <div className="mb-4">
                <h3 className="text-xl font-semibold text-blue-900 border-b-2 border-orange-500 pb-2 mb-4">
                  The Next Generation Migration Platform
                </h3>
                <p className="text-gray-700 mb-4 text-justify">
                  Nexus is a groundbreaking AI-powered platform that transforms
                  how organizations migrate from legacy systems to Workday. By
                  automating complex mapping and configuration processes, Nexus
                  dramatically reduces implementation time, minimizes errors,
                  and ensures a smooth transition.
                </p>

                <div className="flex justify-between text-center mt-6">
                  <div>
                    <div className="text-4xl font-bold text-orange-500">
                      75%
                    </div>
                    <div className="text-xs text-gray-600 uppercase">
                      Faster Migration
                    </div>
                  </div>
                  <div>
                    <div className="text-4xl font-bold text-orange-500">
                      90%
                    </div>
                    <div className="text-xs text-gray-600 uppercase">
                      Error Reduction
                    </div>
                  </div>
                  <div>
                    <div className="text-4xl font-bold text-orange-500">
                      60%
                    </div>
                    <div className="text-xs text-gray-600 uppercase">
                      Cost Savings
                    </div>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="text-xl font-semibold text-blue-900 border-b-2 border-orange-500 pb-2 mb-4">
                  Transformation Journey Excellence
                </h3>
                <div className="bg-blue-50 p-4 rounded-lg">
                  {/* Stage 1: Initial Discovery Process */}
                  <div className="mb-4">
                    <div className="flex items-center mb-2">
                      <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-900 font-bold mr-3">
                        40%
                      </div>
                      <div className="flex-1">
                        <div className="flex w-full justify-between items-center">
                          <h4 className="font-semibold text-blue-900">
                            Initial Discovery Process
                          </h4>
                          <div className="bg-blue-600 text-white text-xs px-2 py-1 rounded-sm whitespace-nowrap ml-2">
                            Foundation
                          </div>
                        </div>
                        <p className="text-sm text-gray-700 mt-1">
                          AI-powered analysis utilizing predefined scripts with
                          adaptive capabilities to intelligently process unknown
                          data structures when standard approaches aren't
                          applicable.
                        </p>
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5 ml-2">
                      <div
                        className="bg-blue-600 h-2.5 rounded-full"
                        style={{ width: "40%" }}
                      ></div>
                    </div>
                  </div>

                  <div className="flex justify-center my-2">
                    <ArrowRight className="text-blue-500" />
                  </div>

                  {/* Stage 2: Automated AI Discovery */}
                  <div className="mb-4">
                    <div className="flex items-center mb-2">
                      <div className="w-12 h-12 rounded-full bg-blue-200 flex items-center justify-center text-blue-900 font-bold mr-3">
                        85%
                      </div>
                      <div className="flex-1">
                        <div className="flex w-full justify-between items-center">
                          <h4 className="font-semibold text-blue-900">
                            Automated AI Discovery
                          </h4>
                          <div className="bg-blue-600 text-white text-xs px-2 py-1 rounded-sm whitespace-nowrap ml-2">
                            Foundation
                          </div>
                        </div>
                        <p className="text-sm text-gray-700 mt-1">
                          Our AI identifies gaps and patterns to accelerate the
                          migration process
                        </p>
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5 ml-2">
                      <div
                        className="bg-blue-600 h-2.5 rounded-full"
                        style={{ width: "85%" }}
                      ></div>
                    </div>
                  </div>

                  <div className="flex justify-center my-2">
                    <ArrowRight className="text-blue-500" />
                  </div>

                  {/* Stage 3: Custom Mapping Process */}
                  <div className="mb-4">
                    <div className="flex items-center mb-2">
                      <div className="w-12 h-12 rounded-full bg-blue-300 flex items-center justify-center text-blue-900 font-bold mr-3">
                        90%
                      </div>
                      <div className="flex-1">
                        <div className="flex w-full justify-between items-center">
                          <h4 className="font-semibold text-blue-900">
                            Custom Mapping Process
                          </h4>
                          <div className="flex">
                            <div className="bg-blue-600 text-white text-xs px-2 py-1 rounded-sm whitespace-nowrap ml-1">
                              A&C
                            </div>
                            <div className="bg-blue-600 text-white text-xs px-2 py-1 rounded-sm whitespace-nowrap ml-1">
                              E2E
                            </div>
                          </div>
                        </div>
                        <p className="text-sm text-gray-700 mt-1">
                          Specialized mapping tools bridge remaining gaps for
                          near-complete automation
                        </p>
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5 ml-2">
                      <div
                        className="bg-blue-600 h-2.5 rounded-full"
                        style={{ width: "90%" }}
                      ></div>
                    </div>
                  </div>

                  <div className="flex justify-center my-2">
                    <ArrowRight className="text-blue-500" />
                  </div>

                  {/* Stage 4: Manual Overrides */}
                  <div className="mb-4">
                    <div className="flex items-center mb-2">
                      <div className="w-12 h-12 rounded-full bg-blue-400 flex items-center justify-center text-blue-900 font-bold mr-3">
                        95%
                      </div>
                      <div className="flex-1">
                        <div className="flex w-full justify-between items-center">
                          <h4 className="font-semibold text-blue-900">
                            Manual Overrides
                          </h4>
                          <div className="flex">
                            <div className="bg-blue-600 text-white text-xs px-2 py-1 rounded-sm whitespace-nowrap ml-1">
                              A&C
                            </div>
                            <div className="bg-blue-600 text-white text-xs px-2 py-1 rounded-sm whitespace-nowrap ml-1">
                              E2E
                            </div>
                          </div>
                        </div>
                        <p className="text-sm text-gray-700 mt-1">
                          Final expert adjustments for 95% accuracy before
                          Workday implementation
                        </p>
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5 ml-2">
                      <div
                        className="bg-blue-600 h-2.5 rounded-full"
                        style={{ width: "95%" }}
                      ></div>
                    </div>
                  </div>

                  <div className="flex justify-center my-2">
                    <ArrowRight className="text-blue-500" />
                  </div>

                  {/* Stage 5: Support Parallel and Go-Live */}
                  <div>
                    <div className="flex items-center mb-2">
                      <div className="w-12 h-12 rounded-full bg-green-400 flex items-center justify-center text-green-900 font-bold mr-3">
                        100%
                      </div>
                      <div className="flex-1">
                        <div className="flex w-full justify-between items-center">
                          <h4 className="font-semibold text-green-800">
                            Support Parallel and Go-Live
                          </h4>
                          <div className="flex">
                            <div className="bg-blue-600 text-white text-xs px-2 py-1 rounded-sm whitespace-nowrap ml-2">
                              Parallel
                            </div>
                            <div className="bg-blue-600 text-white text-xs px-2 py-1 rounded-sm whitespace-nowrap ml-1">
                              Go-Live
                            </div>
                          </div>
                        </div>
                        <p className="text-sm text-gray-700 mt-1">
                          Full verification and finalization to achieve 100%
                          accuracy at migration completion
                        </p>
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5 ml-2">
                      <div
                        className="bg-green-600 h-2.5 rounded-full"
                        style={{ width: "100%" }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Comprehensive Configuration Support */}
              <div>
                <h3 className="text-xl font-semibold text-blue-900 border-b-2 border-orange-500 pb-2 mb-4">
                  Nexus Migration Accelerators
                </h3>
                <ul className="space-y-3 text-sm text-gray-700">
                  <li className="flex items-start">
                    <Clock
                      size={16}
                      className="text-orange-500 mr-2 mt-1 flex-shrink-0"
                    />
                    <span className="text-justify">
                      <span className="font-semibold">
                        Rapid Foundational Data Migration:
                      </span>{" "}
                      Foundation data migration completed in
                      <br />{" "}
                      <span className="text-blue-700 font-semibold">
                        4 weeks
                      </span>{" "}
                      instead of the typical{" "}
                      <span className="text-blue-700 font-semibold">
                        3-4 months
                      </span>
                      , eliminating the primary bottleneck in traditional
                      implementations.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <Search
                      size={16}
                      className="text-orange-500 mr-2 mt-1 flex-shrink-0"
                    />
                    <span className="text-justify">
                      <span className="font-semibold">
                        Intelligent Data Discovery:
                      </span>{" "}
                      Advanced analytics automatically identify and locate
                      critical employee and financial data across complex legacy
                      systems.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <RefreshCw
                      size={16}
                      className="text-orange-500 mr-2 mt-1 flex-shrink-0"
                    />
                    <span className="text-justify">
                      <span className="font-semibold">
                        Pre-Built System Integrations:
                      </span>{" "}
                      <span className="text-blue-700 font-semibold">
                        Proprietary integration scripts
                      </span>{" "}
                      for Oracle JDE, PeopleSoft, and SAP, with{" "}
                      <span className="text-blue-700 font-semibold">
                        100+ integration scripts
                      </span>{" "}
                      and AI-powered data extraction for unknown systems.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <Server
                      size={16}
                      className="text-orange-500 mr-2 mt-1 flex-shrink-0"
                    />
                    <span className="text-justify">
                      <span className="font-semibold">
                        Flexible Data Ingestion:
                      </span>{" "}
                      <span className="text-blue-700 font-semibold">
                        50+ predefined templates
                      </span>{" "}
                      allow clients to upload and integrate data directly,
                      providing multiple pathways for successful data migration.
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Right Column */}
            <div>
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-blue-900 border-b-2 border-orange-500 pb-2 mb-4">
                  Powerful Migration Modules
                </h3>

                <div className="space-y-4">
                  <div className="flex">
                    <div className="bg-blue-50 w-10 h-10 flex items-center justify-center rounded mr-3 flex-shrink-0">
                      <Search className="text-blue-900" size={20} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-blue-900">
                        Discovery Dashboard
                      </h4>
                      <p className="text-sm text-gray-600">
                        AI-powered analysis of current systems to identify
                        structure, data patterns, and migration opportunities.
                      </p>
                    </div>
                  </div>

                  <div className="flex">
                    <div className="bg-blue-50 w-10 h-10 flex items-center justify-center rounded mr-3 flex-shrink-0">
                      <Database className="text-blue-900" size={20} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-blue-900">
                        Data Mapping Visualizer
                      </h4>
                      <p className="text-sm text-gray-600">
                        Interactive side-by-side comparison of Legacy System and
                        Workday System, allowing users to view, edit, and refine
                        data mapping relationships through an intuitive visual
                        interface.
                      </p>
                    </div>
                  </div>

                  <div className="flex">
                    <div className="bg-blue-50 w-10 h-10 flex items-center justify-center rounded mr-3 flex-shrink-0">
                      <RefreshCw className="text-blue-900" size={20} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-blue-900">
                        Learning Model Demonstration
                      </h4>
                      <p className="text-sm text-gray-600">
                        See how our AI continuously improves with each
                        implementation, refining migration patterns.
                      </p>
                    </div>
                  </div>

                  <div className="flex">
                    <div className="bg-blue-50 w-10 h-10 flex items-center justify-center rounded mr-3 flex-shrink-0">
                      <Server className="text-blue-900" size={20} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-blue-900">
                        Workday Integration Simulator
                      </h4>
                      <p className="text-sm text-gray-600">
                        Preview data in Workday format before migration to
                        ensure accuracy and completeness.
                      </p>
                    </div>
                  </div>

                  <div className="flex">
                    <div className="bg-blue-50 w-10 h-10 flex items-center justify-center rounded mr-3 flex-shrink-0">
                      <Clock className="text-blue-900" size={20} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-blue-900">
                        Timeline Comparison Tool
                      </h4>
                      <p className="text-sm text-gray-600">
                        Compare traditional vs. Nexus implementation timelines
                        for measurable project acceleration.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Migration Process section with the updated diagram based on the provided image */}
              <div>
                <h3 className="text-xl font-semibold text-blue-900 border-b-2 border-orange-500 pb-2 mb-4">
                  Nexus Migration Process
                </h3>
                <div className="flex justify-start px-0">
                  <img
                    src="/migration_process.png"
                    alt="Nexus Integration Workflow Process"
                    className="w-full h-auto object-fill"
                    style={{ maxWidth: "90%", marginLeft: "0" }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Key Benefits - Full Width Row with minimal spacing */}
          <div className="w-full mb-0 mt-6">
            <h3 className="text-xl font-semibold text-blue-900 border-b-2 border-orange-500 pb-2 mb-4">
              Key Benefits
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-gray-50 border-l-4 border-orange-500 p-4">
                <div className="font-semibold text-blue-800 mb-2 text-lg">
                  Accelerated Timeline
                </div>
                <p className="text-gray-700">
                  Reduce implementation time from 12-18 months to just 3-4
                  months with AI-assisted configuration.
                </p>
              </div>
              <div className="bg-gray-50 border-l-4 border-orange-500 p-4">
                <div className="font-semibold text-blue-800 mb-2 text-lg">
                  Intelligent Gap Analysis
                </div>
                <p className="text-gray-700">
                  Automatically identify and resolve data inconsistencies and
                  structural gaps between systems.
                </p>
              </div>
              <div className="bg-gray-50 border-l-4 border-orange-500 p-4">
                <div className="font-semibold text-blue-800 mb-2 text-lg">
                  Seamless Integration
                </div>
                <p className="text-gray-700">
                  Pre-built connectors for Oracle JDE, SAP, PeopleSoft, and
                  other legacy systems.
                </p>
              </div>
              <div className="bg-gray-50 border-l-4 border-orange-500 p-4">
                <div className="font-semibold text-blue-800 mb-2 text-lg">
                  Comprehensive Data
                </div>
                <p className="text-gray-700">
                  Complete mapping of organizational structures, compensation
                  data, and employee records.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="bg-gray-50 p-4 border-t border-gray-200 flex justify-between items-center">
          <div>
            <h3 className="text-xl font-semibold text-blue-900">
              Ready to revolutionize your Workday implementation?
            </h3>
            <p className="text-gray-600">
              Schedule a 30-minute demo to see how Nexus can transform your
              migration experience.
            </p>
          </div>
          <button
            className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 px-6 rounded shadow"
            onClick={() => onNavigate("discovery")}
          >
            Start Discovery
          </button>
        </div>

        {/* Footer */}
        <div className="p-4 text-center text-gray-500 text-sm">
          © 2025 WorkCliff | www.workcliff.ca | info@workcliff.ca | (416)
          816-8441
        </div>
      </div>
    </div>
  );
};

export default NexusBrochure;
